import { useQuery } from '@tanstack/react-query';
import {
  Row,
  Col,
  Typography,
  Spin,
  Empty,
  Progress,
  Collapse,
  Space,
  Tooltip,
} from 'antd';
import {
  FireFilled,
  BookFilled,
  AimOutlined,
  StarFilled,
  CrownFilled,
  RocketFilled,
  ThunderboltFilled,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import { useAuthStore } from '@/stores/authStore';
import type {
  ApiResponse,
  StudentAnalytics,
  StudentProgress,
  StudentBadges,
} from '@/types';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStageBg(stage: string): { bg: string; text: string; border: string } {
  const map: Record<string, { bg: string; text: string; border: string }> = {
    BEGINNER:   { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
    FAMILIAR:   { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    PROFICIENT: { bg: '#cffafe', text: '#0e7490', border: '#67e8f9' },
    ADVANCED:   { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
    MASTER:     { bg: '#fef9c3', text: '#a16207', border: '#fde047' },
  };
  return map[stage] ?? map.BEGINNER;
}

function getStreakMessage(streak: number): string {
  if (streak === 1) return 'Day 1 — great start! 🌱';
  if (streak < 4)   return 'Building momentum! Keep going 💪';
  if (streak < 7)   return "You're on a roll! 🚀";
  if (streak < 14)  return "You're on FIRE! Don't stop! 🔥";
  return 'Legendary streak! 👑';
}

const WEEKLY_COLORS = ['#a5b4fc', '#c084fc', '#f9a8d4', '#fdba74', '#6ee7b7', '#7dd3fc', '#fde68a'];

// ---------------------------------------------------------------------------
// Stat cards config
// ---------------------------------------------------------------------------

const STAT_CARDS: Array<{
  key: keyof StudentAnalytics;
  title: string;
  emoji: string;
  iconColor: string;
  bg: string;
  border: string;
  valueColor: string;
  suffix?: string;
  formatter?: (val: number) => string;
}> = [
  {
    key: 'currentStreak',
    title: 'Day Streak',
    emoji: '🔥',
    iconColor: '#e11d48',
    bg: '#fff1f2',
    border: '#fecdd3',
    valueColor: '#be123c',
  },
  {
    key: 'totalCardsReviewed',
    title: 'Cards Reviewed',
    emoji: '🃏',
    iconColor: '#6366f1',
    bg: '#eef2ff',
    border: '#c7d2fe',
    valueColor: '#4f46e5',
  },
  {
    key: 'averageComprehensionRate',
    title: 'Comprehension',
    emoji: '🧠',
    iconColor: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    valueColor: '#15803d',
    suffix: '%',
    formatter: (val: number) => `${Math.round(val * 100)}`,
  },
  {
    key: 'totalDecksCompleted',
    title: 'Decks Done',
    emoji: '📚',
    iconColor: '#0284c7',
    bg: '#f0f9ff',
    border: '#bae6fd',
    valueColor: '#0369a1',
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  emoji,
  title,
  value,
  suffix,
  bg,
  border,
  valueColor,
}: {
  emoji: string;
  title: string;
  value: string | number;
  suffix?: string;
  bg: string;
  border: string;
  valueColor: string;
}) {
  return (
    <div
      style={{
        background: bg,
        border: `1.5px solid ${border}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 26, lineHeight: 1 }}>{emoji}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: valueColor, lineHeight: 1.1 }}>
          {value}
          {suffix && <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 2 }}>{suffix}</span>}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, fontWeight: 500 }}>{title}</div>
      </div>
    </div>
  );
}

function BadgeCard({ badge }: {
  badge: { id: string; name: string; description: string; color: string; icon?: string; earnedAt?: string };
}) {
  const emoji = badge.icon ?? '🏅';
  return (
    <Tooltip title={badge.description}>
      <div
        style={{
          textAlign: 'center',
          background: `${badge.color}14`,
          borderRadius: 12,
          border: `1.5px solid ${badge.color}40`,
          padding: '12px 8px',
          cursor: 'default',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 12px ${badge.color}30`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 4 }}>{emoji}</div>
        <div style={{ fontWeight: 700, fontSize: 12, color: '#334155', lineHeight: 1.3 }}>
          {badge.name}
        </div>
        {badge.earnedAt && (
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
            {new Date(badge.earnedAt).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
          </div>
        )}
      </div>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProgressPage() {
  const { user } = useAuthStore();

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['student', 'analytics', user?.id],
    queryFn: () =>
      api.get<never, ApiResponse<StudentAnalytics>>(
        `${API_ENDPOINTS.ANALYTICS_STUDENTS}/${user?.id}`,
      ),
    enabled: !!user?.id,
  });

  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ['student', 'progress', user?.id],
    queryFn: () =>
      api.get<never, ApiResponse<StudentProgress>>(API_ENDPOINTS.STUDENT_PROGRESS),
    enabled: !!user?.id,
  });

  const { data: badgesData, isLoading: badgesLoading } = useQuery({
    queryKey: ['student', 'badges', user?.id],
    queryFn: () =>
      api.get<never, ApiResponse<StudentBadges>>(API_ENDPOINTS.STUDENT_BADGES),
    enabled: !!user?.id,
  });

  const isLoading = analyticsLoading || progressLoading || badgesLoading;
  const analytics = analyticsData?.data;
  const progress = progressData?.data;
  const badges = badgesData?.data;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 100, gap: 12 }}>
        <div style={{ fontSize: 36 }}>🚀</div>
        <Spin size="large" />
        <Text style={{ fontSize: 14, color: '#6366f1' }}>Loading your progress...</Text>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>🌟 My Progress</div>
          <Text type="secondary" style={{ fontSize: 13 }}>Track your learning journey.</Text>
        </div>
        <div
          style={{
            borderRadius: 16,
            textAlign: 'center',
            padding: '48px 24px',
            background: '#f8faff',
            border: '2px dashed #c7d2fe',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 10 }}>🎮</div>
          <Empty
            image={null}
            description={
              <div>
                <Text style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 700, color: '#4f46e5' }}>
                  No progress yet — let&apos;s go!
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Start reviewing decks to unlock badges and streaks!
                </Text>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  const completionPercent =
    analytics.totalDecksAssigned > 0
      ? Math.round((analytics.totalDecksCompleted / analytics.totalDecksAssigned) * 100)
      : 0;

  const dailyGoalPercent =
    progress && progress.dailyGoalCards > 0
      ? Math.min(100, Math.round((progress.cardsCompletedToday / progress.dailyGoalCards) * 100))
      : 0;

  const displayName = user?.name?.split(' ')[0] ?? 'there';

  return (
    <div style={{ paddingBottom: 24 }}>

      {/* ── Page header ─────────────────────────────────────── */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
            🌟 Hey {displayName}!
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {analytics.currentStreak === 0 && analytics.totalCardsReviewed === 0
              ? 'Start your first deck to begin your adventure! 🎉'
              : analytics.currentStreak === 0
                ? 'Ready to start a new streak today? 💪'
                : 'Every card you review makes you smarter! 🚀'}
          </div>
        </div>
      </div>

      {/* ── Streak highlight bar (compact) ──────────────────── */}
      {analytics.currentStreak > 0 && (
        <div
          style={{
            marginBottom: 16,
            background: '#fff1f2',
            border: '1.5px solid #fecdd3',
            borderRadius: 14,
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <FireFilled style={{ fontSize: 28, color: '#e11d48' }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#be123c' }}>
              {analytics.currentStreak} day streak!&nbsp;
            </span>
            <span style={{ fontSize: 13, color: '#9f1239' }}>
              {getStreakMessage(analytics.currentStreak)}
            </span>
          </div>
          {progress && (
            <div style={{ textAlign: 'center', paddingLeft: 12, borderLeft: '1px solid #fecdd3' }}>
              <div style={{ fontSize: 11, color: '#be123c', fontWeight: 600 }}>Best</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#be123c' }}>
                {progress.longestStreak}d
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Stat cards row ──────────────────────────────────── */}
      <Row gutter={[12, 12]}>
        {STAT_CARDS.map((stat) => {
          const rawValue = analytics[stat.key] as number;
          const displayValue = stat.formatter ? stat.formatter(rawValue) : rawValue;
          return (
            <Col xs={12} sm={12} lg={6} key={stat.key}>
              <StatCard
                emoji={stat.emoji}
                title={stat.title}
                value={displayValue}
                suffix={stat.suffix}
                bg={stat.bg}
                border={stat.border}
                valueColor={stat.valueColor}
              />
            </Col>
          );
        })}
      </Row>

      {/* ── Secondary stats: XP + Decks ring + Daily goal ───── */}
      {progress && (
        <Row gutter={[12, 12]} style={{ marginTop: 12 }} align="stretch">

          {/* XP / Mileage */}
          <Col xs={24} sm={8} style={{ display: 'flex' }}>
            <div
              style={{
                background: '#faf5ff',
                border: '1.5px solid #e9d5ff',
                borderRadius: 14,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
              }}
            >
              <ThunderboltFilled style={{ fontSize: 24, color: '#9333ea' }} />
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed', lineHeight: 1.1 }}>
                  {progress.mileageScore}
                  <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 3 }}>XP</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, fontWeight: 500 }}>
                  Mileage Score
                </div>
              </div>
            </div>
          </Col>

          {/* Deck completion ring */}
          <Col xs={24} sm={8} style={{ display: 'flex' }}>
            <div
              style={{
                background: '#fff',
                border: '1.5px solid #e0e7ff',
                borderRadius: 14,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                width: '100%',
              }}
            >
              <Progress
                type="circle"
                percent={completionPercent}
                size={58}
                strokeColor={{ '0%': '#818cf8', '100%': '#4f46e5' }}
                strokeWidth={9}
                format={(pct) => (
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#4f46e5' }}>{pct}%</span>
                )}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Decks Done</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                  {analytics.totalDecksCompleted} / {analytics.totalDecksAssigned}
                </div>
                <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, marginTop: 3 }}>
                  <RocketFilled style={{ marginRight: 3 }} />
                  {analytics.totalDecksAssigned - analytics.totalDecksCompleted > 0
                    ? `${analytics.totalDecksAssigned - analytics.totalDecksCompleted} left!`
                    : 'All done! 🎉'}
                </div>
              </div>
            </div>
          </Col>

          {/* Daily goal */}
          <Col xs={24} sm={8} style={{ display: 'flex' }}>
            <div
              style={{
                background: '#f0fdf4',
                border: '1.5px solid #bbf7d0',
                borderRadius: 14,
                padding: '14px 16px',
                width: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <AimOutlined style={{ fontSize: 16, color: '#16a34a' }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: '#15803d' }}>Daily Goal</span>
                {dailyGoalPercent >= 100 && <span style={{ fontSize: 15 }}>🎉</span>}
              </div>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#16a34a' }}>
                  {progress.cardsCompletedToday}
                </span>
                <span style={{ color: '#94a3b8' }}> / {progress.dailyGoalCards} cards</span>
              </div>
              <Progress
                percent={dailyGoalPercent}
                strokeColor={{ '0%': '#4ade80', '100%': '#16a34a' }}
                trailColor="#dcfce7"
                strokeLinecap="round"
                size="small"
                showInfo={false}
              />
              <div style={{ marginTop: 5, fontSize: 11, color: '#15803d', fontWeight: 600 }}>
                {dailyGoalPercent >= 100
                  ? '🌟 Goal crushed!'
                  : dailyGoalPercent >= 50
                    ? 'Halfway there! 💪'
                    : `${progress.dailyGoalCards - progress.cardsCompletedToday} more to go!`}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ── Badges ──────────────────────────────────────────── */}
      {badges && badges.earned.length > 0 && (
        <div
          style={{
            marginTop: 16,
            background: '#fffbeb',
            border: '1.5px solid #fde68a',
            borderRadius: 14,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CrownFilled style={{ fontSize: 18, color: '#d97706' }} />
            <span style={{ fontSize: 14, fontWeight: 800, color: '#92400e' }}>Your Badges</span>
            <span
              style={{
                background: '#fbbf24',
                color: '#fff',
                borderRadius: 20,
                padding: '1px 8px',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {badges.earned.length}
            </span>
          </div>
          <Row gutter={[10, 10]}>
            {badges.earned.map((badge) => (
              <Col key={badge.id} xs={8} sm={6} md={4} lg={3}>
                <BadgeCard badge={badge} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* ── Weekly Activity ─────────────────────────────────── */}
      {progress && progress.weeklyActivity.length > 0 && (
        <div
          style={{
            marginTop: 16,
            background: '#fff',
            border: '1.5px solid #e2e8f0',
            borderRadius: 14,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <StarFilled style={{ fontSize: 16, color: '#f59e0b' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
              This Week&apos;s Activity
            </span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={progress.weeklyActivity} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(d: string) =>
                  new Date(d).toLocaleDateString('en', { weekday: 'short' })
                }
              />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RechartsTooltip
                contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                formatter={(value: number) => [value, 'Cards']}
                labelFormatter={(label: string) =>
                  new Date(label).toLocaleDateString('en', { weekday: 'long', month: 'short', day: 'numeric' })
                }
              />
              <Bar dataKey="cardsReviewed" radius={[6, 6, 0, 0]} name="Cards Reviewed">
                {progress.weeklyActivity.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={WEEKLY_COLORS[i % WEEKLY_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Subject Mastery ─────────────────────────────────── */}
      {progress && progress.conceptProgress.length > 0 && (
        <div
          style={{
            marginTop: 16,
            background: '#fff',
            border: '1.5px solid #e2e8f0',
            borderRadius: 14,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BookFilled style={{ fontSize: 16, color: '#0284c7' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Subject Mastery</span>
          </div>
          <Collapse ghost expandIconPosition="end">
            {progress.conceptProgress.map((subject) => {
              const stageMeta = getStageBg(subject.averageStage);
              return (
                <Collapse.Panel
                  key={subject.subjectId}
                  style={{
                    marginBottom: 8,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: '#f8faff',
                    border: '1px solid #e0e7ff',
                  }}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                        {subject.subjectName}
                      </span>
                      <span
                        style={{
                          background: stageMeta.bg,
                          color: stageMeta.text,
                          border: `1px solid ${stageMeta.border}`,
                          borderRadius: 20,
                          padding: '1px 8px',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {subject.averageStage}
                      </span>
                    </div>
                  }
                >
                  <div style={{ paddingLeft: 4 }}>
                    {subject.chapters.map((ch) => {
                      const chMeta = getStageBg(ch.masteryStage);
                      return (
                        <div
                          key={ch.chapterId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #f0f4ff',
                            flexWrap: 'wrap',
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 13, color: '#374151', fontWeight: 500, minWidth: 100 }}>
                            {ch.chapterName}
                          </span>
                          <Space wrap>
                            <Progress
                              percent={Math.round(ch.comfortScore)}
                              size="small"
                              style={{ width: 100 }}
                              strokeColor={{ '0%': '#818cf8', '100%': '#4f46e5' }}
                              strokeLinecap="round"
                            />
                            <span
                              style={{
                                background: chMeta.bg,
                                color: chMeta.text,
                                border: `1px solid ${chMeta.border}`,
                                borderRadius: 20,
                                padding: '1px 8px',
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {ch.masteryStage}
                            </span>
                          </Space>
                        </div>
                      );
                    })}
                  </div>
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </div>
      )}
    </div>
  );
}
